import React, {useState, useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CancelIcon from '@mui/icons-material/CancelRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructionsRounded';
import CodeIcon from '@mui/icons-material/CodeRounded';
import Tooltip from '@mui/material/Tooltip';

const Level = ({data, levelNum, onChange, onDelete, onEdit}) => {
    return (
        <Box 
            sx={{
                p: (levelNum === 0) ? 0 : '6px 12px', 
                width: data?.items ? '100%' : 'auto', 
                mb:'0.5rem!important', 
                border: (levelNum === 0 ? 'none' : '1px solid rgba(0, 0, 0, 0.1)'), 
                borderRadius: '8px',
            }}
        >
            <Stack direction="row" sx={{width: '100%'}}>
                {levelNum > 0 ?
                <InputLabel 
                    sx={{cursor: 'pointer', transform: 'scale(0.75)'}}
                    onClick={() => {
                        if (data) {
                            data.invert = data.invert ? false : true
                            onChange(data)
                        }
                    }}
                >
                    {data?.invert === true ?
                    <b><u>NOT</u></b>
                    : <b><u><s>NOT</s></u></b> }
                </InputLabel>
                : null }
                
                <InputLabel 
                    sx={{cursor: 'pointer', transform: 'scale(0.75)'}}
                    onClick={() => {
                        if (data) {
                            data.operation = data.operation === 'AND' ? 'OR' : 'AND'
                            onChange(data)
                        }
                    }}
                >
                    <b><u>Group {data?.operation}</u></b>
                </InputLabel>
                <Stack sx={{flexGrow:1}} />
                {levelNum > 0 ?
                <IconButton sx={{p:0, mr:'-4px', mt: '-4px'}}
                    onClick={() => {
                        onDelete(data)
                    }}
                >
                    <CancelIcon sx={{fontSize: '16px'}} />
                </IconButton>
                : null }
            </Stack>
            <Stack 
                direction="row"
                spacing={0.25}
                sx={{flexWrap: 'wrap', alignItems: 'start' }}
            >
                {data?.items?.map((row, key) => {
                    if (row.items) {
                        return (
                            <Level key={key} data={row} levelNum={levelNum + 1} onChange={(newData) => {
                                row = newData
                                onChange(data)
                            }} onDelete={(item) => {
                                let i = data.items.indexOf(item)
                                if (i > -1) {
                                    data.items.splice(i, 1)
                                    onChange(data)
                                }
                            }} onEdit={onEdit} />
                        )
                    } else {
                        if (row?.item) {
                            return (
                                <Chip 
                                    key={key}
                                    label={(row.invert ? 'NOT ' : '') + row?.item} 
                                    size="small" 
                                    sx={{m: '0px!important', mr:'0.25rem!important', mb:'0.25rem!important'}}
                                    onClick={() => {
                                        onEdit(data, row)
                                    }}
                                    onDelete={() => {
                                        let i = data.items.indexOf(row)
                                        if (i > -1) {
                                            data.items.splice(i, 1)
                                            onChange(data)
                                        }
                                    }}
                                />
                            )
                        } else {
                            return null
                        }
                    }
                })}

                {data?.items ?
                <ButtonGroup
                    size="small"
                    variant="contained"
                >
                    <Tooltip title="Add Single Attribute" placement="top">
                        <Button
                            onClick={() => {
                                onEdit(data, null)
                            }}
                            sx={{p:0,mt: '-1px'}}
                        >
                            ADD
                        </Button>
                    </Tooltip>
                    <Tooltip title="Add a Subgroup" placement="top">
                        <Button 
                            sx={{p:0,mt: '-1px'}}
                            color="warning"
                            onClick={() => {
                                data.items.push({
                                    operation: 'OR',
                                    items: []
                                })
                                onChange(data)
                            }}
                        >
                            <CodeIcon />
                        </Button>
                    </Tooltip>
                </ButtonGroup>
                : null }
            </Stack>
        </Box>
    )
}

export default function QueryBuilder(props) {
    const [tree, setTree] = useState()
    const [updateCounter, setUpdateCounter] = useState(0)
    const [editData, setEditData] = useState()
    const [queryLength, setQueryLength] = useState(props.query?.length ?? 0)
    const inputRef = useRef()
    const notRef = useRef()
    
    const getParts = (query) => {
        if (!query) return [];
        
        let parts = []
        
        let s = ''
        for (let i = 0; i < query.length; i++) {
            let c = query[i]
            let i2 = -1
            let o = ''
            switch (c) {
                case '(':
                case ')':
                    if (s) {
                        parts.push(s)
                        s = ''
                    }
                    parts.push(c)
                    break;
                case '"':
                    i2 = query.indexOf('"', i + 1)
                    if (i2 === -1) {
                        i2 = query.length
                    }
                    s = query.substr(i, i2 - i + 1).trim()
                    if (s) {
                        parts.push(s)
                        s = ''
                    }
                    i = i2
                    break;
                case ' ':
                    if (query.substr(i + 1, 2) === 'OR') {
                        o = 'OR'
                    } else
                    if (query.substr(i + 1, 3) === 'AND') {
                        o = 'AND'
                    }
                
                    if (o) {
                        if (s) {
                            parts.push(s)
                            s = ''
                        }
                        parts.push(o)
                        i += o.length + 1
                    } else {
                        if (s[s.length - 1] !== ' ') {
                            s += c
                        }
                    }
                    break;
                default:
                    if (c === 'N' && query.substr(i, 3) === 'NOT') {
                        if (s) {
                            parts.push(s)
                            s = ''
                        }
                        parts.push('NOT')
                        i += 3
                    } else {
                        s += c
                    }
            }
        }
        
        if (s) {
            parts.push(s)
        }
        
        return parts
    }
    
    const parse = (query) => {
        let parts = getParts(query)
        
        const parseBlock = (from) => {
            const operations = ['OR', 'AND']
            
            let end = parts.length - 1;
            let level = []
            let operation = ''
            for (let i = from; i < parts.length; i++) {
                let p = parts[i]
                if (p === '(') {  // Start Child Block
                    let invert = false
                    if (parts[i - 1] === 'NOT') {
                        invert = true
                    }
                    let b = parseBlock(i + 1)
                    i = b.end
                    level.push({
                        operation: b.operation,
                        invert: invert,
                        items: b.level
                    })
                } else
                if (p === ')') {  // End Current Block
                    end = i
                    break;
                } else
                if (p === 'NOT') {
                    if (parts[i + 1] !== '(') {
                        level.push({
                            invert: true,
                            item: parts[i + 1].replace(/"+/g, '').trim()
                        })
                        i += 1
                    }
                } else {
                    if (operations.indexOf(p) === -1) {
                        level.push({
                            invert: false,
                            item: p.replace(/"+/g, '').trim(),
                        })
                    } else
                    if (operation === '') {
                        operation = p
                    }
                }
            }
            
            if (!operation) {
                operation = 'OR'
            }
            
            return {
                operation: operation,
                level: level,
                end: end,
            }
        }
        
        let p = parseBlock(0)
        
        return {
            operation: p.operation,
            items: p.level
        }
    }
    
    const doChange = (data) => {
        const make = (level, num) => {
            let res = ''
            if (level.items) {
                let a = []
                level.items?.forEach((item) => {
                    a.push(make(item, num + 1))
                })
                if (num > 0) {
                    res = '(' + a.join(' ' + level.operation + ' ') + ')'
                } else {
                    res = a.join(' ' + level.operation + ' ')
                }
            } else {
                res = level.item?.replace(/"+/g, '');
                if (res.indexOf(' ') > -1) {
                    res = '"' + res + '"'
                }
            }
            
            return (level.invert ? 'NOT ' : '') + res;
        }
        const s = make(data, 0)
        setQueryLength(s?.length ?? 0)
        props.onChange(s)
    }
    
    const saveAttribute = () => {       
        if (!inputRef.current.value) return ;
        
        if (editData?.item) {
            editData.item.invert = notRef.current.checked
            editData.item.item = inputRef.current.value.replace(/"+/g, '')
        } else {
            editData.parent.items.push({
                invert: notRef.current.checked,
                item: inputRef.current.value.replace(/"+/g, ''),
            })
        }
        doChange(tree)
        setEditData(null)
    }
    
    useEffect(() => {
        setTree(parse(props.query))
    }, [props.query])
    
    useEffect(() => {
        if (editData) {
            setTimeout(() => {
                inputRef.current.focus()
            }, 0)
        }
    }, [editData])
    
    return (
        <Stack>
            <Box sx={{
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                padding: '25px 12px 8px',
                position: 'relative',
            }}>
                <InputLabel sx={{ transformOrigin: 'top left', position: 'absolute', left: 0, top: 0, transform: 'translate(12px, 7px) scale(0.75)'}}>Query</InputLabel>
                <InputLabel sx={{ transformOrigin: 'top right', position: 'absolute', right: 0, top: 0, transform: 'translate(-12px, 7px) scale(0.75)' }}>Length: {queryLength}</InputLabel>
                <Stack
                    className="dialogFieldFullHeight"
                    fullheightoffset="134"
                    sx={{ overflow: 'auto' }}
                >
                    <Level 
                        data={tree} 
                        levelNum={0} 
                        onChange={(newData) => {
                            setTree(newData)
                            setUpdateCounter(updateCounter + 1)
                            doChange(newData)
                        }} 
                        onDelete={(item) => {
                            let newData = {
                                operation: 'AND',
                                invert: false,
                                items: []
                            }
                            setTree(newData)
                            doChange(newData)
                        }}
                        onEdit={(parent, item) => {
                            setEditData({
                                parent: parent,
                                item: item,
                            })
                        }}
                    />
                </Stack>
            </Box>
            {props.error ?
            <FormHelperText error="true" sx={{ml: '14px'}}>{props.error}</FormHelperText>
            : null }
            <Dialog open={editData ? true : false} onClose={() => {setEditData(null)}} fullWidth maxWidth="xs">
                <form onSubmit={(e) => {e.preventDefault()}}>
                    <DialogTitle>
                        Single Attribute Properties
                        <IconButton
                            onClick={() => {setEditData(null)}}
                            sx={{
                              position: 'absolute',
                              right: 8,
                              top: 8,
                              color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={0}>
                            <FormControlLabel control={<Checkbox defaultChecked={editData?.item?.invert} inputRef={notRef} />} label="NOT" />
                            <TextField
                                autoFocus
                                fullWidth 
                                name="text" 
                                label="Text" 
                                variant="filled" 
                                defaultValue={editData?.item?.item}
                                inputRef={inputRef}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {setEditData(null)}}>Cancel</Button>
                        <Button type="submit" onClick={() => {saveAttribute()}}>Save</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Stack>
    );
}